import Store from "./store";
import { action } from "mobx";
import { SectionQuiz, SavedSection, SAVED_SECTIONS_KEY, SectionFinalQuiz } from "../types";
import { findItemAndIndexById } from "../utils";
import { QuestionAggregate, getQuestion, isQuestionValid, isLastQuestion } from "@upandgo/react-quiz";
import { createWrapper } from "@upandgo/scorm-wrapper";
import content from "../Content";
import cloneDeep from "lodash/cloneDeep";
import { SCORMWrapper } from "@upandgo/scorm-wrapper/dist/types";
import axios from "axios";

export default class Actions {
  private store: Store;
  private scormAPI: SCORMWrapper;

  constructor(props: { store: Store }) {
    this.store = props.store;
    this.scormAPI = createWrapper();
  }

  sendSCORMStatsToServer = async (props: { url: string; identity: any; scormState: any }) => {
    await axios.post(props.url, {
      applicationId: this.store.content.idApplication,
      user: props.identity,
      scormState: props.scormState,
    });
  };

  closeScorm = () => {
    this.scormAPI.close();
  };

  @action
  completeSection = (sectionId: string) => {
    if (!this.store.completedSectionIds.includes(sectionId)) this.store.completedSectionIds.push(sectionId);
  };

  @action
  seeResource = (resourceId: string) => {
    if (!this.store.alreadySeenResourceIds.includes(resourceId)) this.store.alreadySeenResourceIds.push(resourceId);
  };

  getQuizWithSelectedAnswers = (quiz: QuestionAggregate, idQuestion: string, idAnswers: string[]) => {
    const newQuiz = { ...quiz };
    const questionSearch = findItemAndIndexById(idQuestion, quiz.questionList);
    const question = questionSearch.item;
    for (let i = 0, n = question.answerList.length; i < n; i++) {
      question.answerList[i].selected = idAnswers.includes(question.answerList[i].id);
    }
    newQuiz.questionList[questionSearch.index] = question;
    return newQuiz;
  };

  @action
  selectFinalQuizAnswer = async (idQuestion: string, idAnswers: string[]) => {
    const finalQuizIndex = this.store.content.sections.findIndex((sec) => sec.type === "finalQuiz");
    (this.store.content.sections[finalQuizIndex] as SectionFinalQuiz).quiz = {
      ...this.getQuizWithSelectedAnswers(this.store.finalQuiz.quiz, idQuestion, idAnswers),
    };
    const suspendData = JSON.parse((await this.scormAPI.getBaseData("suspendData")) as string) || {};
    await this.scormAPI.setBaseData(
      "suspendData",
      JSON.stringify({
        ...suspendData,
        finalQuiz: {
          questionList: this.store.finalQuiz.quiz.questionList
            .filter((question) => question.answerList.some((answer) => answer.selected))
            .map((question) => ({
              id: question.id,
              answerList: question.answerList.filter((answer) => answer.selected).map((answer) => answer.id),
            })),
        },
      }),
    );
    if (isLastQuestion(this.store.finalQuiz.quiz, idQuestion)) {
      await this.scormAPI.setBaseData("completeStatus", "completed");
    }
  };

  @action
  resetFinalQuizAnswers = async () => {
    await this.scormAPI.setBaseData("completeStatus", "incomplete");
    const suspendData = JSON.parse((await this.scormAPI.getBaseData("suspendData")) as string) || {};
    suspendData.finalQuiz = {};
    await this.scormAPI.setBaseData("suspendData", JSON.stringify(suspendData));
    this.store.savedFinalQuiz = {
      questionList: [],
    };
    const finalQuizIndex = content.sections.findIndex((sec) => sec.type === "finalQuiz");
    this.store.content.sections[finalQuizIndex] = cloneDeep(content.sections[finalQuizIndex]);
  };

  @action
  fetchSavedSection = async () => {
    const suspendData = JSON.parse((await this.scormAPI.getBaseData("suspendData")) as string) || {};
    const savedSections: { [key: string]: SavedSection } = suspendData[SAVED_SECTIONS_KEY] || {};
    this.store.savedSections = savedSections;
  };

  @action
  fetchSavedFinalQuiz = async () => {
    const suspendData = JSON.parse((await this.scormAPI.getBaseData("suspendData")) as string) || {};
    this.store.savedFinalQuiz = {
      ...this.store.savedFinalQuiz,
      ...suspendData.finalQuiz,
    };
    for (let i = 0, n = this.store.savedFinalQuiz.questionList.length; i < n; i++) {
      await this.selectFinalQuizAnswer(
        this.store.savedFinalQuiz.questionList[i].id,
        this.store.savedFinalQuiz.questionList[i].answerList,
      );
    }
  };

  @action
  fetchCompletionStatus = async () => {
    const status = await this.scormAPI.getBaseData("completeStatus");
    this.store.completionStatus = status as any;
  };

  @action
  updateSavedSection = async (idSection: string, save: Partial<Omit<SavedSection, "id">>) => {
    const sectionSearch = findItemAndIndexById(idSection, this.store.content.sections);
    if (sectionSearch.item && !sectionSearch.item.storeBookmark) return;
    const currentSuspendData = JSON.parse((await this.scormAPI.getBaseData("suspendData")) as string) || {};
    const suspendData = typeof currentSuspendData === "object" ? currentSuspendData : {};
    const savedSections: { [key: string]: SavedSection } = suspendData[SAVED_SECTIONS_KEY] || {};
    const savedSection: SavedSection = {
      ...{
        done: false,
        validQuestionList: [],
        primaryResourceSeen: [],
        secondaryResourceSeen: [],
      },
      ...savedSections[idSection],
    };
    const newSavedSections = {
      ...(suspendData[SAVED_SECTIONS_KEY] || {}),
      [idSection]: { ...savedSection, ...save },
    };
    suspendData[SAVED_SECTIONS_KEY] = newSavedSections;
    await this.scormAPI.setBaseData("suspendData", JSON.stringify(suspendData));
    this.store.savedSections = newSavedSections;
  };

  @action
  selectAnswers = async (idSection: string, idQuestion: string, idAnswers: string[]) => {
    await this.fetchSavedSection();
    const sectionSearch = findItemAndIndexById(idSection, this.store.content.sections);

    if (sectionSearch.item.type === "quiz") {
      (this.store.content.sections[sectionSearch.index] as SectionQuiz).quiz = this.getQuizWithSelectedAnswers(
        (this.store.content.sections[sectionSearch.index] as SectionQuiz).quiz,
        idQuestion,
        idAnswers,
      );
      const question = getQuestion((this.store.content.sections[sectionSearch.index] as SectionQuiz).quiz, idQuestion);
      if (isQuestionValid(question.item)) {
        const savedSection: SavedSection = {
          ...{
            done: false,
            validQuestionList: [],
            primaryResourceSeen: [],
            secondaryResourceSeen: [],
          },
          ...this.store.savedSections[idSection],
        };
        await this.updateSavedSection(idSection, {
          validQuestionList: [...savedSection.validQuestionList, idQuestion],
        });
      }
    }
  };
}
